// Three-tier trim ladder: stream-copy → re-encode → passthrough.
// Stream-copy is ~20-100x faster than re-encode and uses far less memory.
// We fall back silently to keep export reliability.

import { ffmpegRemux, ffmpegTrimToMp4 } from "./ffmpeg-convert";
import { devLog, devWarn } from "./logger";

export interface TrimResult {
  blob: Blob;
  trimmed: boolean;
  engine: "remux" | "ffmpeg" | "passthrough";
  reason?: string;
}

const MIN_TRIM_DURATION = 0.2;
const MIN_OUTPUT_BYTES = 1000;

export async function clientTrimMp4(
  file: File | Blob,
  trimStart: number,
  trimEnd: number,
  _totalDuration: number,
  onProgress?: (msg: string) => void,
): Promise<TrimResult> {
  if (
    !isFinite(trimStart) ||
    !isFinite(trimEnd) ||
    trimEnd <= trimStart ||
    trimEnd - trimStart < MIN_TRIM_DURATION
  ) {
    devWarn("[trim] invalid range, returning original", { trimStart, trimEnd });
    return { blob: file, trimmed: false, engine: "passthrough", reason: "invalid-range" };
  }

  // Tier 1: stream-copy remux (fast path).
  try {
    const blob = await ffmpegRemux(file, trimStart, trimEnd, onProgress);
    if (blob && blob.size >= MIN_OUTPUT_BYTES) {
      devLog("[trim] remux ok", { size: blob.size });
      return { blob, trimmed: true, engine: "remux" };
    }
    throw new Error("remux produced invalid/empty output");
  } catch (err) {
    devWarn("[trim] remux failed, falling back to re-encode", err);
  }

  // Tier 2: full re-encode (slow but reliable).
  try {
    onProgress?.("Re-encoding…");
    const blob = await ffmpegTrimToMp4(file, trimStart, trimEnd, onProgress);
    if (blob && blob.size >= MIN_OUTPUT_BYTES) {
      return { blob, trimmed: true, engine: "ffmpeg" };
    }
    throw new Error("ffmpeg produced empty/invalid output");
  } catch (err) {
    devWarn("[trim] re-encode failed, returning original", err);
    return { blob: file, trimmed: false, engine: "passthrough", reason: "ffmpeg-failed" };
  }
}
